import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { task } from 'ember-concurrency'
import { isBefore } from 'date-fns'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  type SortingConfig,
} from '@blakeelearning/data-tables/utils/sorting'
import {
  serialize as serializeSelected,
  deserialize as deserializeSelected,
} from '@blakeelearning/data-tables/utils/selecting'
import type Store from '@ember-data/store'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type { Log } from '@blakeelearning/log'
import type DeleteClassesRoute from './route'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type { RowId } from 'teacher-ui-client/components/stable-table/basic-table/cell'

export default class DeleteClassesController extends Controller<ModelFor<DeleteClassesRoute>> {
  @service store!: Store

  @service subscriptionType!: SubscriptionTypeService

  @service log!: Log

  queryParams = ['pageNumber', 'perPage', 'selected', 'sort']

  @tracked
  selected = ''

  @tracked
  sort = 'createdAt'

  @tracked
  pageNumber = '1'

  @tracked
  perPage = '20'

  @tracked
  schoolClasses: SchoolClass[] = []

  @tracked
  schoolClassesToDelete: SchoolClass[] = []

  get rolloverCreatedAt() {
    return this.model.rollover?.createdAt
  }

  fetchPreRolloverClasses = task({ drop: true }, async () => {
    const { subscriptionType, rolloverCreatedAt } = this

    try {
      const allSchoolClasses = await this.store.query<SchoolClass>('school-class', {
        // @ts-expect-error - include types not correct
        include: ['teachers', 'students'].join(','),
      })

      if (!rolloverCreatedAt) {
        throw new Error('no rollover created at date')
      }
      // Only need to present classes that are for this sub type, and are before the rollover was started. This is to
      // prevent the teacher from deleting classes they may have created as part of this rollover.
      const preRolloverSchoolClasses = allSchoolClasses.filter((schoolClass) => {
        const isMatchingSubscriptionType = schoolClass.subscriptionType === subscriptionType.current
        const isPreRollover = isBefore(schoolClass.createdAt, rolloverCreatedAt)
        return isMatchingSubscriptionType && isPreRollover
      })

      // Set the new classes, and set array of selected item ids only including these classes

      const schoolClassIds = preRolloverSchoolClasses.map((schoolClass) => String(schoolClass.id))
      const newSelectedItemIds = this.selectedItemIds.filter((id) => schoolClassIds.includes(id))

      this.schoolClasses = preRolloverSchoolClasses
      this.selectedItemIds = newSelectedItemIds
    } catch (e: any) {
      this.log.error('failed to fetch pre rollover school classes', e)
      this.schoolClasses = []
      this.selectedItemIds = []
    }
  })

  get selectedClasses() {
    return this.schoolClasses.filter((schoolClass) => schoolClass.id && this.selectedItemIds.includes(schoolClass.id))
  }

  /**
   * A list of school classes that are not deleted, avoids attempting to render school classes that are being deleted or
   * in flight.
   */
  get schoolClassesForTable() {
    return this.schoolClasses.filter((sc) => !sc.isDeleted)
  }

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  get paginationConfig() {
    return { pageNumber: Number(this.pageNumber), perPage: Number(this.perPage) }
  }

  get selectedItemIds() {
    return deserializeSelected(this.selected, String)
  }

  set selectedItemIds(newSelectedIds) {
    this.selected = serializeSelected(newSelectedIds)
  }

  @action
  updateSelected(itemIds: RowId[]) {
    this.selectedItemIds = [...itemIds.filter((itemId): itemId is string => typeof itemId === 'string')]
  }

  @action
  updateSorts(sortingConfig: SortingConfig) {
    this.sortingConfig = sortingConfig
  }

  @action
  setSchoolClassesToDelete(schoolClasses: SchoolClass[]) {
    this.schoolClassesToDelete = schoolClasses
  }

  @action
  afterPromptAction({ refreshClasses = false } = {}) {
    if (refreshClasses) void this.fetchPreRolloverClasses.perform()
    this.setSchoolClassesToDelete([])
  }
}
